import { ASYNC_STATUS_ID } from 'constants/index'
import { useCallback, useEffect, useState } from 'react'
import { useAPI } from 'utils/hooks/useAPI'
import { API } from 'constants/apiEndPoints'
import { isAddressesEqual } from 'utils/wallet/addresses'
import { useUser } from 'hooks/user/useUser'
import { useLvgState } from 'hooks/games/lvg/useLvgState'
import { capitalizeFirst, isStockMarketOpen } from 'utils'
import { useYoloToast } from 'lib/yoloToasts/useYoloToast'
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events'
import { EVENTS } from 'constants/events'
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import { icons } from 'common'
import { LvgTwitterShareBid } from 'components/Molecules/LvgTwitterSharedBid.button'
import { LVG_ORDER_STATE } from 'constants/games/lvg/lvgOrderState'
import axios from 'axios'
import { useLocation, useNavigate } from 'react-router-dom'
import { checkCountryAllowance } from 'hooks/user/useCountryAllowanceObserver'
import { v4 as uuid } from 'uuid'
import ms from 'ms.macro'
import { usePriceFeed } from 'hooks/usePriceFeed'
import { getPnL } from 'utils/pnl'
import { CONTENT_TYPES } from 'constants/games/contentTypes'
import { getAssetTypeByAssetId } from 'constants/games/lvg/lvgAssets'
import { getAssetInfo } from 'constants/assets'
import { useSocketListener } from 'hooks/sockets/useSocket'

const ALLOWED_STATES = [LVG_ORDER_STATE.PENDING, LVG_ORDER_STATE.LIVE, LVG_ORDER_STATE.CLOSED, LVG_ORDER_STATE.BUSTED, LVG_ORDER_STATE.FUNDING]

const toastSuccessLiveObj = {
  id: 'successToast',
  autoClose: true,
  props: {
    message: {
      title: 'Your bid has been placed successfully',
      subtitle: `to track your bid and cash out`,
      showTable: true,
    },
  },
}
const toastUpdateLiveObj = {
  id: 'successToast',
  autoClose: true,
  props: {
    message: {
      title: 'Your bid has been updated successfully',
      subtitle: `to track your bid and cash out`,
      showTable: true,
    },
  },
}

const getToastSuccessClosedObj = (order) => ({
  id: 'announceToast',
  autoClose: ms`4s`,
  props: {
    icon: icons.bid_success_black_icon,
    message: {
      title: <strong>Cash out was successful</strong>,
      subtitle: (
        <>
          Your balance will be updated shortly
          <br />
          <LvgTwitterShareBid order={order} fromToast />
        </>
      ),
    },
  },
})

// Atoms Definition
const lvgYourOrdersCacheAtom = atom([])
const lvgYourLiveOrdersCacheAtom = atom((get) =>
  get(lvgYourOrdersCacheAtom).filter((order) => order.status === LVG_ORDER_STATE.LIVE)
)
const updateLvgYourOrderCachedAtom = atom(null, (get, set, order) => {
  const { uuid } = order
  if (!uuid) return
  
  set(lvgYourOrdersCacheAtom, (prev) => {
    const orderExists = prev.some((prevOrder) => prevOrder.uuid === uuid)
    if (!orderExists) return [...prev, order]
    
    const updatedOrders = prev.map((prevOrder) => {
      if (prevOrder.uuid === uuid) {
        let trailingIsUpdated = order.trailingIsUpdated        
        if ('trailingStopPercent' in prevOrder &&
        'trailingStopPercent' in order &&
        prevOrder.trailingStopPercent !== order.trailingStopPercent &&
        order.trailingStopPercent) {
        console.log('ACZ 2.4: percent')
        trailingIsUpdated = 'percent'
      }
      if ('trailingStopValue' in prevOrder &&
        'trailingStopValue' in order &&
        prevOrder.trailingStopValue !== order.trailingStopValue &&
          order.trailingStopValue) trailingIsUpdated = 'value'
        return { ...prevOrder, ...order, trailingIsUpdated }
      }
      return prevOrder
    })
    return updatedOrders
  })
})

const removeLvgYourOrderCachedAtom = atom(null, (get, set, uuid) => {
  if (!uuid) return
  set(lvgYourOrdersCacheAtom, (prev) => {
    const updatedOrders = prev.filter((prevOrder) => prevOrder.uuid !== uuid)
    return updatedOrders
  })
})

export const useYourBidsOrders = () => {
  const [yourOrders, setYourOrders] = useAtom(lvgYourOrdersCacheAtom)
  const updateYourOrder = useSetAtom(updateLvgYourOrderCachedAtom)
  const removeYourOrder = useSetAtom(removeLvgYourOrderCachedAtom)
  const [lvgYourState, sendLvgYourQuery, hasLvgYourStatus] = useAPI(API.LVG_ORDERS_LIST, {
    queryType: 'get',
    controlled: true,
    withJwt: true,
  })

  // Filling the cached
  useEffect(() => {
    sendLvgYourQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (hasLvgYourStatus(ASYNC_STATUS_ID.CONFIRMED)) {
      const ownOrdersByStatus = lvgYourState.data

    console.log('ACZ: ownOrdersByStatus',{ownOrdersByStatus})
      const ownOrders = Object.keys(ownOrdersByStatus).reduce(
        (ownOrders, stateArray) => [...ownOrders, ...(ownOrdersByStatus[stateArray] || [])],
        []
      )
      const reviewedOrders = ownOrders
        .filter((order) => !!order.uuid)
        .map((order) => {
          const { status } = order
          if (status.includes('pending')) order.status = LVG_ORDER_STATE.PENDING
          order.assetType = getAssetTypeByAssetId(order?.asset)

          return { ...order, trailingIsUpdated: '' }
        })
        .filter((order) => ALLOWED_STATES.includes(order.status))

      setYourOrders(reviewedOrders)
      // setYourOrdersCached(reviewedOrders)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lvgYourState.status?.id])

  // Listening for updates
  const { account } = useUser('wallet')
  const { activeOrder, setActiveOrder, updateActiveOrder } = useLvgState()
  const { yToast } = useYoloToast()

  const orderUpdateMiddleware = ({ order, noToast }) => {
    
    if (!order.uuid) return
    if (!isAddressesEqual(order.userAddress, account)) return

    const extendedOrder = { ...order, address: order.userAddress, assetType: getAssetTypeByAssetId(order?.asset) }
    const { status } = extendedOrder
      switch (status) {
        case LVG_ORDER_STATE.PENDING:
          break
        case LVG_ORDER_STATE.LIVE:
          setActiveOrder(extendedOrder)
          const isUpdate =
          yourOrders.filter((cachedOrder) => cachedOrder.uuid === order.uuid)?.[0]?.status === LVG_ORDER_STATE.LIVE ||
          false
          yToast(isUpdate ? toastUpdateLiveObj : toastSuccessLiveObj)
          emitCustomEvent(EVENTS.LVG_ORDER_LIVE, extendedOrder)
          break
  
        case LVG_ORDER_STATE.CLOSING:
          break
        case LVG_ORDER_STATE.CLOSED:
          yToast(getToastSuccessClosedObj(order))
          emitCustomEvent(EVENTS.LVG_ORDER_CLOSED, extendedOrder)
          break
  
        case LVG_ORDER_STATE.CANCELLED:
          const toastErrorCancelledObj = {
            id: 'errorToast',
            autoClose: false,
            props: {
              message: {
                title: capitalizeFirst(extendedOrder?.details || extendedOrder?.reason || 'The order has been cancelled'),
                subtitle: 'Please try again',
              },
            },
          }
          yToast(toastErrorCancelledObj)
          emitCustomEvent(EVENTS.LVG_ORDER_CANCELLED, extendedOrder)
          break
  
        case LVG_ORDER_STATE.BUSTED:
          const toastWarningBustedObj = {
            id: 'warningToast',
            autoClose: true,
            props: {
              message: {
                title: capitalizeFirst(extendedOrder?.details || extendedOrder?.reason || 'The order was busted'),
                subtitle: 'Please try again',
              },
            },
          }
          yToast(toastWarningBustedObj)
          emitCustomEvent(EVENTS.LVG_ORDER_BUSTED, extendedOrder)
          break
  
        default:
          break
      }
    //activeOrder, setActiveOrder
    if (activeOrder.uuid === extendedOrder.uuid) setActiveOrder(extendedOrder)
    updateYourOrder(extendedOrder)
  }

  const orderApiErrorMiddleware = ({ apiResponse, order, event }) => {
    const getToastErrorObj = (apiResponse) => ({
      id: 'errorToast',
      autoClose: false,
      props: {
        message: {
          title: capitalizeFirst(apiResponse?.error || 'Something went wrong, please contact us'),
          subtitle: 'Please try again',
        },
      },
    })
    switch (event) {
      case EVENTS.LVG_ORDER_PENDING_ERROR:
        yToast(getToastErrorObj(apiResponse))
        removeYourOrder(order.uuid)
        break
      case EVENTS.LVG_ORDER_CLOSING_ERROR:
        yToast(getToastErrorObj(apiResponse))
        updateYourOrder({ ...order, status: LVG_ORDER_STATE.LIVE })
        break
      case EVENTS.LVG_ORDER_UPDATING_ERROR:
        yToast(getToastErrorObj(apiResponse))
        break
      default:
        break
    }
  }
  
  /* prettier-ignore */ useCustomEventListener(EVENTS.LVG_ORDER_PENDING, orderUpdateMiddleware)
  /* prettier-ignore */ useCustomEventListener(EVENTS.LVG_ORDER_CLOSING, orderUpdateMiddleware)
  /* prettier-ignore */ useCustomEventListener(EVENTS.LVG_ORDER_UPDATING, orderUpdateMiddleware)

  /* prettier-ignore */ useCustomEventListener(EVENTS.LVG_ORDER_PENDING_ERROR, (payload) => orderApiErrorMiddleware({ ...payload, event: EVENTS.LVG_ORDER_PENDING_ERROR }))
  /* prettier-ignore */ useCustomEventListener(EVENTS.LVG_ORDER_CLOSING_ERROR, (payload) => orderApiErrorMiddleware({ ...payload, event: EVENTS.LVG_ORDER_CLOSING_ERROR }))
  /* prettier-ignore */ useCustomEventListener(EVENTS.LVG_ORDER_UPDATING_ERROR, (payload) => orderApiErrorMiddleware({ ...payload, event: EVENTS.LVG_ORDER_UPDATING_ERROR }))

  /* prettier-ignore */ useSocketListener('gateway','order.public', (order) => orderUpdateMiddleware({ order }))
  /* prettier-ignore */ useSocketListener('gateway','order.private.trailingstop.update', (trailingUpdate) => {
    const order = yourOrders.filter(item => item.uuid === trailingUpdate.uuid)
    const updatedOrder = { ...order.at(0), trailingStopPrice: trailingUpdate.newTrailingStopPrice }
    if (activeOrder.uuid === updatedOrder.uuid) updateActiveOrder(updatedOrder)
    updateYourOrder(updatedOrder)
  })
  /* prettier-ignore */ useSocketListener('gateway','order.error', (order) => orderUpdateMiddleware({ order: { ...order, status: LVG_ORDER_STATE.ERROR } }))

  return { yourBidsOrders: yourOrders }
}

//TODO: improvement: use a listener of price feed channel to update the portfolio balance and remove heartbeat
export const useLvgLiveOrdersAmount = () => {
  const lvgLiveOrders = useAtomValue(lvgYourLiveOrdersCacheAtom)
  const { getPriceFeed, priceFeedHeartbeat } = usePriceFeed()
  const [liveOrdersAmount, setLiveOrdersAmount] = useState({
    potentialPnl: 0,
    potentialAmount: 0,
    winningOrders: 0,
    lossingOrders: 0,
  })

  const updatePortfolioBalance = () => {
    const portfolioBalance = lvgLiveOrders
      .map((order) => {
        const { asset, entryPrice, side, fillQuantity, leverage, amount } = order
        const currentPrice = getPriceFeed(asset)?.price || 0
        const pnl = getPnL(entryPrice, currentPrice, side, fillQuantity, leverage, asset)
        const balance = amount + pnl

        return { balance, pnl }
      })
      .reduce(
        (obj, item) => ({
          potentialPnl: obj.potentialPnl + item.pnl,
          potentialAmount: obj.potentialAmount + item.balance,
          winningOrders: item.pnl > 0 ? obj.winningOrders + 1 : obj.winningOrders,
          lossingOrders: item.pnl <= 0 ? obj.lossingOrders + 1 : obj.lossingOrders,
        }),
        { potentialPnl: 0, potentialAmount: 0, winningOrders: 0, lossingOrders: 0 }
      )
    setLiveOrdersAmount(portfolioBalance)
  }

  useEffect(() => {
    if (lvgLiveOrders.length > 0) {
      updatePortfolioBalance()
      return
    }
    setLiveOrdersAmount({ potentialAmount: 0, potentialPnl: 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceFeedHeartbeat])

  return {
    liveOrdersAmount: {
      potentialPnl: liveOrdersAmount.potentialPnl,
      potentialAmount: liveOrdersAmount.potentialAmount,
    },
    liveOrdersCount: { winningOrders: liveOrdersAmount.winningOrders, lossingOrders: liveOrdersAmount.lossingOrders },
  }
}

// ---------------------------------------------------------------------------
// ORDER MANAGER
// ---------------------------------------------------------------------------

const sendNewOrder = (rawOrder, accessToken) =>
  axios
    .post(API.LVG_TRADING_CREATE_ORDER, rawOrder, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    .then((res) => res.data)

const sendCloseOrder = (liveOrder, accessToken) =>
  axios
    .post(API.LVG_TRADING_CLOSE_ORDER, liveOrder, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    .then((res) => res.data)

const sendUpdateOrder = (orderObj, accessToken) =>
  axios
    .post(API.LVG_TRADING_UPDATE_ORDER, orderObj, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    .then((res) => res.data)

export const useLvgOrderManager = () => {
  const navigateTo = useNavigate()
  const { account, accessToken } = useUser('wallet')
  const location = useLocation()
  const lvgLiveOrders = useAtomValue(lvgYourLiveOrdersCacheAtom)

  const sendOrder = useCallback(
    (orderObj) => {
      checkCountryAllowance().then(async (payload) => {
        if (!payload.isAllowed) navigateTo(`/game/${CONTENT_TYPES.RESTRICTED}`)
        else {
          const rawOrder = { ...orderObj, uuid: uuid() }
          const pendingOrder = {
            ...rawOrder,
            userAddress: account,
            status: LVG_ORDER_STATE.PENDING,
          }
          emitCustomEvent(EVENTS.LVG_ORDER_PENDING, { order: pendingOrder })
          const apiResponse = await sendNewOrder(rawOrder, accessToken)
          if (apiResponse.status === 'error') {
            emitCustomEvent(EVENTS.LVG_ORDER_PENDING_ERROR, {
              apiResponse,
              order: pendingOrder,
            })
          }
        }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account, accessToken, location]
  )

  const closeOrder = useCallback(
    async (liveOrder) => {
      const closingOrder = {
        ...liveOrder,
        userAddress: account,
        status: LVG_ORDER_STATE.CLOSING,
      }
      emitCustomEvent(EVENTS.LVG_ORDER_CLOSING, { order: closingOrder })
      const apiResponse = await sendCloseOrder(liveOrder, accessToken)
      if (apiResponse.status === 'error') {
        emitCustomEvent(EVENTS.LVG_ORDER_CLOSING_ERROR, {
          apiResponse,
          order: closingOrder,
        })
      }
    },
    [accessToken, account]
  )

  const updateOrder = useCallback(
    async (updateObj) => {
      const apiResponse = await sendUpdateOrder(updateObj, accessToken)
      if (apiResponse.status === 'error') {
        emitCustomEvent(EVENTS.LVG_ORDER_UPDATING_ERROR, {
          apiResponse,
          order: updateObj,
        })
      }
    },
    [accessToken]
  )

  const closeAllOrders = useCallback(() => {
    lvgLiveOrders.forEach((order) => {
      const assetInfo = getAssetInfo(order.asset)

      if (assetInfo.hasTime && !isStockMarketOpen().state) return
      closeOrder(order)
    })
  }, [lvgLiveOrders, closeOrder])

  return { sendOrder, updateOrder, closeOrder, closeAllOrders }
}
